import { enter, leave } from 'el-transition'
import { Controller } from '@hotwired/stimulus'
import { get } from '@rails/request.js'

export default class extends Controller {
  static outlets = ['clients--document-upload-form']

  static targets = [
    'dragZone',
    'fileList',
    'fileTemplate',
    'fileUploadContainer',
    'fileUploadPlaceholder',
    'hiddenFileInput',
    'submit',
    'submittingPlaceholder',
    'successPlaceholder',
  ]

  static values = {
    folderId: String,
    historical: { default: false, type: Boolean },
    state: { default: 'waitingForTemplate', type: String },
    templateUrl: String,
    uploadType: String,
  }

  connect() {
    this.dragOverStyle = []

    if (this.hasDragZoneTarget) {
      const { dragOverStyle } = this.dragZoneTarget.dataset
      if (dragOverStyle) this.dragOverStyle = dragOverStyle.split(' ')
    }
  }

  applyDragOverStyles() {
    this.dragZoneTarget.classList.add(...this.dragOverStyle)
  }

  clientsDocumentUploadFormOutletConnected() {
    this.stateValue = 'active'
  }

  clientsDocumentUploadFormOutletDisconnected() {
    if (['active', 'submitting'].includes(this.stateValue)) {
      if (!this.hasClientsDocumentUploadFormOutlet) {
        this.stateValue = 'waitingForFileUpload'
      } else {
        this.#checkFormsForAllSuccess()
      }
    }
  }

  folderIdValueChanged() {
    this.fileListTarget.innerHTML = ''
    this.stateValue = 'waitingForTemplate'

    if (this.folderIdValue) this.#fetchTemplate()
  }

  handleDragOver(event) {
    event.preventDefault()
  }

  handleDrop(event) {
    event.preventDefault()
    this.removeDragOverStyles()

    if (event.dataTransfer.items) {
      [...event.dataTransfer.items].forEach((item) => {
        if (item.kind === 'file') {
          this.#addFileForm(item.getAsFile())
        }
      })
    } else {
      [...event.dataTransfer.files].forEach((file) => this.#addFileForm(file))
    }
  }

  handleFileInput(event) {
    const { files } = event.target

    if (files.length) {
      [...files].forEach((file) => this.#addFileForm(file))
    }
  }

  handleFolderChange({ detail: { folderId } }) {
    if (this.historicalValue) {
      this.#show()
    } else {
      this.folderIdValue = folderId
    }
  }

  handleModalHide() {
    if (this.stateValue === 'success') {
      this.folderIdValue = ''
    }
  }

  openFileInput() {
    this.hiddenFileInputTarget.click()
  }

  removeDragOverStyles() {
    this.dragZoneTarget.classList.remove(...this.dragOverStyle)
  }

  async submitForm({ detail: { submitPromise } }) {
    await submitPromise

    this.#checkFormsForAllSuccess()
  }

  async submitAll() {
    if (this.stateValue === 'active') {
      this.stateValue = 'submitting'

      const promises = this.clientsDocumentUploadFormOutlets.map(
        (form) => form.submit(),
      )

      await Promise.allSettled(promises)

      this.#checkFormsForAllSuccess()
    }
  }

  stateValueChanged() {
    switch (this.stateValue) {
      case 'active':
        this.submitTarget.innerHTML = this.submitTarget.dataset.activeText
        this.submitTarget.disabled = false
        this.dispatch('uploadStateChange', { detail: { disableInput: false } })
        break
      case 'submitting':
        this.submitTarget.innerHTML = this.submitTarget.dataset.submittingText
        this.submitTarget.disabled = true
        this.#show(
          this.fileListTarget,
          this.submittingPlaceholderTarget,
          this.submitTarget,
        )
        this.dispatch('uploadStateChange', { detail: { disableInput: true } })
        break
      case 'success':
        this.#show(
          this.fileListTarget,
          this.successPlaceholderTarget,
          this.submitTarget,
        )
        this.dispatch('uploadStateChange', { detail: { disableInput: false } })
        this.dispatch(
          'reloadFolder',
          { detail: { folderId: this.folderIdValue } },
        )
        break
      case 'waitingForFileUpload':
        this.submitTarget.innerHTML =
          this.submitTarget.dataset.waitingForFileUploadText
        this.submitTarget.disabled = true
        this.#show(
          this.fileListTarget,
          this.fileUploadContainerTarget,
          this.submitTarget,
        )
        this.dispatch('uploadStateChange', { detail: { disableInput: false } })
        break
      case 'waitingForTemplate':
      default:
        this.#show(this.fileUploadPlaceholderTarget)
    }
  }

  #addFileForm(originalFile) {
    if (this.hasFileTemplateTarget) {
      const file = new File(
        [originalFile],
        Valence.Utils.File.sanitizeFilename(originalFile.name),
        { type: originalFile.type },
      )
      const clone = this.fileTemplateTarget.content.cloneNode(true)
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      clone.querySelector('input[type=file]').files = dataTransfer.files
      this.fileListTarget.appendChild(clone)
    }
  }

  #checkFormsForAllSuccess() {
    const allSuccess = this.clientsDocumentUploadFormOutlets.every(
      (form) => form.statusValue === 'success',
    )

    if (allSuccess) {
      this.stateValue = 'success'
    }
  }

  async #fetchTemplate() {
    let response

    if (this.abortController) {
      this.abortController.abort('new filter request')
    }

    this.abortController = new AbortController()

    try {
      response = await get(
        this.templateUrlValue,
        {
          query: {
            folder_id: this.folderIdValue,
            upload_type: this.uploadTypeValue,
          },
          responseKind: 'json',
          signal: this.abortController.signal,
        },
      )
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      }

      throw error
    }

    if (!response.ok) {
      throw new Error(response.status)
    }

    const data = await response.json

    this.fileListTarget.innerHTML = data.results
    this.stateValue = 'waitingForFileUpload'
  }

  #show(...elements) {
    [
      this.fileListTarget,
      this.fileUploadContainerTarget,
      this.fileUploadPlaceholderTarget,
      this.submitTarget,
      this.submittingPlaceholderTarget,
      this.successPlaceholderTarget,
    ].filter((el) => !elements.includes(el)).
      forEach((element) => leave(element))

    elements.forEach((element) => enter(element))
  }
}
